import { useEffect, useMemo, useState } from 'react';
import { Empty, ErrorBanner, Field, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { formatNumber } from '../../lib/format';
import { useHeldResource } from '../../lib/hooks';

interface Contract {
  id: number;
  name: string;
  number: string | null;
  type: string | null;
  start: string | null;
  end: string | null;
}
interface ContractService {
  id: number;
  contractId: number;
  contract: string;
  service: string;
  period: string | null;
  units: number | null;
  unitPrice: number | null;
  unitCost: number | null;
  total: number | null;
}
interface ServicesPayload {
  linked: boolean;
  companyName: string | null;
  unitsKnown?: boolean;
  contracts: Contract[];
  services: ContractService[];
  fetchedUtc?: string;
}

const money = (v: number | null) => (v === null ? '—' : formatNumber(v, 'currency'));
const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/* Months in each Autotask billing period. A period not here (one-time, or one
   Autotask will not name) is not recurring and stays out of monthly revenue. */
const MONTHS: Record<string, number> = { monthly: 1, quarterly: 3, 'semi-annual': 6, semiannual: 6, yearly: 12, annual: 12, annually: 12 };
const monthsIn = (period: string | null) => (period ? MONTHS[period.trim().toLowerCase()] : undefined);

/** What a set of services comes to a month: monthly lines as they are, yearly ones ÷ 12, and so on. */
const monthlyOf = (services: ContractService[]) =>
  Math.round(services.reduce((sum, s) => sum + (s.total !== null && monthsIn(s.period) ? s.total / monthsIn(s.period)! : 0), 0) * 100) / 100;

/** Totals per billing period, largest first — a monthly and a yearly figure are not one number. */
const totalsByPeriod = (services: ContractService[]) => {
  const m = new Map<string, number>();
  for (const s of services) if (s.total !== null) m.set(s.period ?? 'Other', (m.get(s.period ?? 'Other') ?? 0) + s.total);
  return [...m].sort((a, b) => b[1] - a[1]);
};

/**
 * The client's services: everything on its ACTIVE Autotask contracts, read
 * live through the client's Autotask company (the link the Assets tab and the
 * review's ticket slides use — routes/amAutotask.ts). Grouped by contract,
 * with units in effect today, price, extended total and cost, and each
 * contract's total. Held until Refresh.
 *
 * The one thing stored: what every contract comes to a month (monthly lines,
 * plus yearly ÷ 12, quarterly ÷ 3, semi-annual ÷ 6) is written to the client's
 * Monthly revenue whenever it differs from what is there. Not when units could
 * not be read — that total would be short — and not when there is nothing
 * priced, so a figure typed in by hand is not wiped by an empty read.
 */
export function AmClientServices({
  clientId,
  clientName,
  monthlyRevenue,
  onMonthlyRevenue,
}: {
  clientId: string;
  clientName: string;
  monthlyRevenue: number | null;
  onMonthlyRevenue: () => void;
}) {
  const data = useHeldResource<ServicesPayload>(`/am/clients/${clientId}/autotask/services`);
  const [search, setSearch] = useState('');

  const d = data.data;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.services ?? []).filter((s) => !q || [s.service, s.contract, s.period].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [d, search]);

  const byPeriod = useMemo(() => totalsByPeriod(d?.services ?? []), [d]);
  const monthly = useMemo(() => monthlyOf(d?.services ?? []), [d]);
  const priced = (d?.services ?? []).some((s) => s.total !== null && monthsIn(s.period));

  /* Keep the client's Monthly revenue in step with its contracts. A viewer
     without edit rights is refused by the API; the tab still shows the figure. */
  useEffect(() => {
    if (!d?.linked || d.unitsKnown === false || !priced) return;
    if (Math.abs(monthly - (monthlyRevenue ?? 0)) < 0.005) return;
    api.put(`/am/clients/${clientId}`, { monthlyRevenue: monthly }).then(onMonthlyRevenue, () => {});
  }, [d, priced, monthly, monthlyRevenue, clientId, onMonthlyRevenue]);

  if (data.loading && !d) return <Loading label="Reading the client's contracts from Autotask" />;
  if (data.error) return <ErrorBanner message={data.error} />;
  if (!d) return null;
  if (!d.linked) {
    return (
      <Empty title="Not linked to Autotask">
        <p>This client has no Autotask company linked, so there are no contracts to read.</p>
      </Empty>
    );
  }

  const contracts = d.contracts.filter((c) => rows.some((s) => s.contractId === c.id) || !search.trim());
  const exportCsv = () =>
    downloadCsv(`${slugForFile(clientName)}_services_${fileStamp()}.csv`, [
      ['Contract', 'Service', 'Period', 'Units', 'Unit price', 'Total', 'Unit cost'],
      ...rows.map((s) => [s.contract, s.service, s.period, s.units, s.unitPrice, s.total, s.unitCost]),
    ]);

  return (
    <div className="mt-4">
      <div className="row-between wrap mb-2">
        <p className="xs muted">
          {d.contracts.length} active contract{d.contracts.length === 1 ? '' : 's'} for {d.companyName} in Autotask · {d.services.length} service
          {d.services.length === 1 ? '' : 's'}
          {byPeriod.length > 0 && ` · ${byPeriod.map(([p, t]) => `${money(t)} ${p.toLowerCase()}`).join(' · ')}`}
          {priced && (
            <>
              {' · '}
              <strong>{money(monthly)} a month</strong> in all
            </>
          )}
          {d.unitsKnown === false && ' · units could not be read from Autotask'}
        </p>
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
          <Field label="Search">
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Service or contract" />
          </Field>
          <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
            Export CSV
          </button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
            {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
          </button>
        </div>
      </div>

      {d.contracts.length === 0 ? (
        <Empty title="No active contracts">
          <p>{d.companyName} has no active contract in Autotask.</p>
        </Empty>
      ) : (
        contracts.map((c) => {
          const lines = rows.filter((s) => s.contractId === c.id);
          const all = d.services.filter((s) => s.contractId === c.id);
          const periods = totalsByPeriod(all);
          return (
            <section key={c.id} className="card mb-4">
              <div className="card-head">
                <div className="grow">
                  <h3>{c.name || 'Contract'}</h3>
                  <p className="sub">
                    {[c.number, c.type, `${day(c.start)} – ${c.end ? day(c.end) : 'no end date'}`].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {periods.length > 0 && (
                  <div className="num">
                    <div className="strong">{money(monthlyOf(all))} a month</div>
                    <div className="xs muted">{periods.map(([p, t]) => `${money(t)} ${p.toLowerCase()}`).join(' · ')}</div>
                  </div>
                )}
              </div>
              {lines.length === 0 ? (
                <div className="card-body">
                  <p className="muted small">No services on this contract{search.trim() ? ' match' : ''}.</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Period</th>
                        <th className="num">Units</th>
                        <th className="num">Unit price</th>
                        <th className="num">Total</th>
                        <th className="num">Unit cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((s) => (
                        <tr key={s.id}>
                          <td>{s.service}</td>
                          <td>{s.period ?? '—'}</td>
                          <td className="num">{s.units === null ? '—' : formatNumber(s.units)}</td>
                          <td className="num">{money(s.unitPrice)}</td>
                          <td className="num strong">{money(s.total)}</td>
                          <td className="num muted">{money(s.unitCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
